import React from 'react';
import { ILoadableDataState } from '../../hooks/use-loadable-data-ref-state';
import { OrgNotificationModel } from '../../models/notification/org-notification.model';
import { AccountDto } from '../../dtos/account.dto';
import { Well } from '../../components/well/well';
import { NotificationSummaryCard } from '@pushly/aqe/lib/components';
import { Col, Row, Button, Tooltip } from 'antd';
import { RetweetOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { StatusType } from '../../enums/status-type';
import { arrayContains, backfillStats, titleCase } from '../../_utils/utils';
import moment from 'moment-timezone';
import { DEFAULT_HIGHCHARTS_CONFIG, INSIGHTS_CLICKS_COLOR, INSIGHTS_IMPS_COLOR } from '../../constants';
import { HighchartsUtils } from '../../_utils/highcharts-utils';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { arrayUnqiue } from '../../_utils/array';
import { InsightsService } from '../../services/insights';
import { Container } from 'typescript-ioc/es5';
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window';
import * as numeral from 'numeral';
import { AppState } from '../../stores/app';
import { extractSummaryNotification } from '../../_utils/notifications';

interface IOrgNotificationSummaryTabProps {
    notifLoader: React.MutableRefObject<ILoadableDataState<OrgNotificationModel | undefined>>
    orgLoader: React.MutableRefObject<ILoadableDataState<AccountDto | undefined>>,
}

interface IState {
    notif?: OrgNotificationModel,
    breakdownStats?: any,
    loadingBreakdownStats?: boolean,
}

class OrgNotificationSummaryTab extends React.Component<IOrgNotificationSummaryTabProps, IState> {
    protected static tabName = 'summary';
    protected static tabLabel = 'Summary';

    protected readonly appState: AppState;
    private readonly insightsService: InsightsService;

    public constructor(props: IOrgNotificationSummaryTabProps) {
        super(props);

        this.appState = Container.get(AppState);
        this.insightsService = Container.get(InsightsService);

        this.state = {};
    }

    public componentDidUpdate() {
        const { notif } = this.state;
        const { notifLoader } = this.props;

        if (!notif && !!notifLoader.current.data) {
            this.setState({ notif: notifLoader.current.data! }, () => {
                this.fetchBreakdownStats();
            });
        }
    }

    public render() {
        const { notifLoader, orgLoader } = this.props;
        const org = orgLoader.current.data;
        const notif = notifLoader.current.data;

        return (
            <>
                <div className="outer-well-buttons">
                    <Button
                        className="refresh-list"
                        size="small"
                        onClick={() => {
                        }}
                    >
                        <RetweetOutlined/>
                        <span>Refresh</span>
                    </Button>
                </div>

                {this.renderGenericStats()}
                <br/>

                <Row gutter={24} className="details-lower-row">
                    <Col span={14}>
                        {this.renderBreakdownStats()}
                    </Col>
                    <Col span={10}>
                        <NotificationSummaryCard
                            mode="light"
                            loading={notifLoader.current.loading}
                            notification={extractSummaryNotification(notif ?? new OrgNotificationModel())}
                            hideStats={true}
                            domain={this.appState.currentDomain!}
                            hideFooter={true}
                        />
                    </Col>
                </Row>
            </>
        );
    }

    protected renderGenericStats() {
        const {notifLoader} = this.props;
        const loading = notifLoader.current.loading;
        const notif = notifLoader.current.data;
        const actions = notif?.getVariant()?.getContent()?.getDefaultContent()?.getActions()?.map(a => a.serialize()) ?? [];
        const hasActions = actions.length > 0;

        actions.sort((a, b) =>
            a.ordinal < b.ordinal ? 1 : a.ordinal < b.ordinal ? -1 : 0);

        return (
            <Well
                className="thin gen-stats-well nested"
                loading={loading}
                showHeader={false}
                showFooter={false}
            >
                <Well
                    ghost={true}
                    showHeader={false}
                    showFooter={false}
                >
                    <div className="chart-title">Status</div>
                    <div className="stat stat-status">
                        {this.renderStatus()}
                    </div>
                </Well>
                <Well
                    ghost={true}
                    showHeader={false}
                    showFooter={false}
                    disabled={this.isDisabled}
                >
                    <div className="chart-title">Impressions</div>
                    <div className="stat stat-impressions">
                        {this.renderImpressions()}
                    </div>
                </Well>
                <Well
                    ghost={true}
                    showHeader={false}
                    showFooter={false}
                    disabled={this.isDisabled}
                >
                    <div className="chart-title">{hasActions && 'Total '}Clicks (CTR)</div>
                    <div className="stat stat-clicks">
                        {this.renderClicks()}
                        <div className="stat stat-ctr sub-stat">
                            ({this.renderCTR()})
                        </div>
                    </div>
                </Well>
            </Well>
        );
    }

    protected renderStatus() {
        const { notifLoader } = this.props;
        const notif = notifLoader.current.data;

        const notifStatus = notif?.getComputedStatus() ?? 'SCHEDULED';
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name];
        const isPartialDelivery = notifStatus === StatusType.COMPLETED_WITH_FAILURES.name;
        let statusText = arrayContains(completedStatuses, notifStatus) ? 'DELIVERED' : notifStatus;
        let statusId = 0;


        // Delivered
        let color: string = 'green';

        const statusLookup = StatusType[statusText];
        if (statusLookup) {
            statusId = statusLookup.id;
        }

        switch (statusId) {
            case StatusType.SCHEDULED.id:
            case StatusType.SCHEDULING.id:
                statusText = 'SCHEDULED';
                color = 'purple';
                break;
            case StatusType.QUEUED.id:
            case StatusType.DELIVERING.id:
                color = 'orange';
                break;
            case StatusType.FAILED.id:
                color = 'red';
                break;
            case -1:
                color = 'volcano';
                break;
        }

        return (
            <div className={`color-${color}`}>
                {titleCase(statusText)}
                {isPartialDelivery && (
                    <Tooltip
                        title="A portion of this notification's audience failed to deliver."
                    >
                        <ExclamationCircleOutlined className="info-icon error-icon" />
                    </Tooltip>
                )}
            </div>
        );
    }

    protected renderBreakdownStats() {
        const { notifLoader } = this.props;
        const { loadingBreakdownStats } = this.state;

        return (
            <Well
                className="thin primary-stats-well"
                showHeader={false}
                showFooter={false}
                loading={notifLoader.current.loading || loadingBreakdownStats}
                disabled={this.isDisabled}
            >
                <div className="chart-header">
                    <div className="chart-title">
                        Performance Summary
                    </div>
                    <div className="chart-actions">
                    </div>
                </div>

                {this.renderBreakdownStatsChart()}
            </Well>
       );
    }

    protected renderBreakdownStatsChart() {
        const { breakdownStats, notif } = this.state;
        const stats = notif?.getStats();

        if (!stats) { return; }
        if (!breakdownStats) { return ''; }

        const xAxisData = breakdownStats.map((stat: any) =>
            moment.tz(stat.event_date, 'UTC').format('MMM D'));

        const impsData = breakdownStats.map((stat: any) => stat.impressions || 0);
        const clicksData = breakdownStats.map((stat: any) => stat.clicks || 0);

        const options = {
            ...DEFAULT_HIGHCHARTS_CONFIG,
            chart: {
                type: 'column',
                marginLeft: 50,
                marginRight: 50,
                marginTop: 10,
                padding: 10,
            },
            title: {
                text: '',
            },
            xAxis: {
                categories: xAxisData,
            },
            yAxis: [
                {
                    type: 'logarithmic',
                    title: { text: '' },
                    startOnTick: 0,
                    minRange: 0,
                },
            ],
            tooltip: {
                pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
                formatter: HighchartsUtils.defaultTooltipFormatter,
                shared: true
            },
            series: [
                {
                    type: 'column',
                    name: 'Impressions',
                    data: impsData,
                    color: INSIGHTS_IMPS_COLOR,
                },
                {
                    type: 'column',
                    name: 'Clicks',
                    data: clicksData,
                    color: INSIGHTS_CLICKS_COLOR,
                },
            ],
        };

        return (
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
            />
        );
    }

    protected renderImpressions() {
        const stats = this.state.notif?.getStats();
        if (this.isDisabled || !stats) { return '--'; }
        return numeral(stats.impressions).format('O,O');
    }

    protected renderClicks() {
        const stats = this.state.notif?.getStats();
        if (this.isDisabled || !stats) { return '--'; }
        return numeral(stats.clicks).format('O,O');
    }

    protected renderCTR() {
        const stats = this.state.notif?.getStats();
        if (this.isDisabled || !stats) { return '--'; }
        return numeral(stats.ctr_decimal).format('0.00%');
    }

    protected get isDelivered(): boolean {
        const { notif } = this.state;
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name];
        return !!notif && (arrayContains(completedStatuses, notif.getComputedStatus() ?? StatusType.SCHEDULED.name) || this.isParitalFailure);
    }

    protected get isParitalFailure(): boolean {
        const { notif } = this.state;
        const stats = notif?.getStats();
        const hasStats = !!stats && stats.impressions > 0;
        return !!notif && (notif.getComputedStatus() === StatusType.FAILED.name && hasStats);
    }

    protected get isDisabled(): boolean {
        const { notif } = this.state;
        const status = notif?.getComputedStatus() ?? StatusType.SCHEDULED.name;
        const delivery = notif?.getVariant()?.getDelivery();

        const isDelivered = this.isDelivered;
        const isDelivering = status === StatusType.DELIVERING.name;
        const isCancelled = status === StatusType.CANCELLED.name ||
            status === StatusType.CANCELLING.name;
        const isInf = delivery?.getWindow() === NotificationDeliveryWindow.INFORMED;
        const isStz = delivery?.getWindow() === NotificationDeliveryWindow.TIMEZONE;

        return !isDelivered && !((isInf || isStz) && (isDelivering || isCancelled));
    }

    protected async fetchBreakdownStats() {
        const { notifLoader } = this.props;
        const notif = notifLoader.current.data;

        if (!notif) { return; }
        this.setState({ loadingBreakdownStats: true });

        const domainIds = arrayUnqiue(notif.getNotifications().map(n => n.domainId));

        const insightsPkg = {
            datePreset: 'lifetime',
            dateIncrement: 'day',
            actionAttribution: 'event_time',
            fields: [
                "notification_group.id",
                "deliveries",
                "impressions",
                "clicks",
                "ctr",
                "ctr_decimal",
                "delivery_rate",
                "delivery_rate_decimal"
            ],
            entity: 'notifications',
            breakdowns: [
                'notification_group'
            ],
            filters: [
                {
                    field: 'domain.id',
                    operator: 'in',
                    value: domainIds
                },
                {
                    field: 'notification_group.id',
                    operator: 'eq',
                    value: notif.getId(),
                }
            ],
            order: {
                field: 'event_date',
                direction: 'asc',
            },
        };

        const breakdownStats = await this.insightsService.fetch(
            insightsPkg, false, 'org-notif-stats', true);

        const backfilledStats = backfillStats(breakdownStats, 'day', {
            deliveries: 0,
            impressions: 0,
            clicks: 0,
            delivery_rate_decimal: 0,
            ctr_decimal: 0,
        });

        this.setState({
            loadingBreakdownStats: false,
            breakdownStats: backfilledStats,
        });
    }
}

export default OrgNotificationSummaryTab;
